import { resourceQueryPredicates } from '../../resources/queries/resourceQueryPredicates.js';
import { useQueryClient } from '@tanstack/react-query';
import { tagsQueryPredicates } from '../queries/tagsQueryPredicates.js';
import { mutateResourceTag } from './mutateResourceTag.js';
import { useOptimisticallyUpdateQueriesWithPredicate, useOptimisticallyUpdateInfiniteQueriesWithPredicate, upsertItemInArrayById } from '@fabric-app/queries/utilities';

const useQueryCacheTagHelpers = () => {
  const queryClient = useQueryClient();
  const optimisticallyUpdateQueries = useOptimisticallyUpdateQueriesWithPredicate();
  const optimisticallyUpdateInfiniteQueries = useOptimisticallyUpdateInfiniteQueriesWithPredicate();
  return {
    updateCachedTag: (tagWithUpdatedProperties) => {
      queryClient.cancelQueries({
        predicate: (q) => tagsQueryPredicates.all(q) || tagsQueryPredicates.tag(q, tagWithUpdatedProperties.id),
        type: "active"
      });
      const optimisticUpdateAllQueries = optimisticallyUpdateQueries(tagsQueryPredicates.all, (data) => upsertItemInArrayById(data ?? [], tagWithUpdatedProperties, {
        createIfNotFound: false
      }));
      const optimisticUpdateDirectQueries = optimisticallyUpdateQueries((q) => tagsQueryPredicates.tag(q, tagWithUpdatedProperties.id), (tag) => tag && {
        ...tag,
        ...tagWithUpdatedProperties
      });
      return {
        optimisticUpdateDirectQueries,
        optimisticUpdateAllQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticUpdateAllQueries.resetQueriesData();
          optimisticUpdateDirectQueries.resetQueriesData();
        },
        invalidateQueries: () => {
          optimisticUpdateAllQueries.invalidateQueries();
          optimisticUpdateDirectQueries.invalidateQueries();
        }
      };
    },
    deleteCachedTag: (tagId) => {
      queryClient.cancelQueries({
        predicate: (q) => tagsQueryPredicates.all(q) || tagsQueryPredicates.tag(q, tagId) || tagsQueryPredicates.resourcesAll(q),
        type: "active"
      });
      const optimisticUpdateAllQueries = optimisticallyUpdateQueries(tagsQueryPredicates.all, (data) => data?.filter((t) => t.id !== tagId));
      const optimisticUpdateDirectQueries = optimisticallyUpdateQueries((q) => tagsQueryPredicates.tag(q, tagId), () => void 0);
      const optimisticUpdateAllResourceQueries = optimisticallyUpdateQueries(tagsQueryPredicates.resourcesAll, (data) => {
        if (!data) {
          return;
        }
        return data.map((resource) => ({
          ...resource,
          tags: resource.tags.filter((t) => t.id !== tagId)
        }));
      });
      const optimisticallyUpdateFilterAndSearchResourceQueries = optimisticallyUpdateInfiniteQueries({
        predicate: resourceQueryPredicates.resourceList,
        pageUpdater: (page) => ({
          ...page,
          resources: page.resources.map((resource) => {
            return {
              ...resource,
              tags: resource.tags.filter((t) => t.id !== tagId)
            };
          })
        })
      });
      return {
        optimisticUpdateDirectQueries,
        optimisticUpdateAllQueries,
        optimisticUpdateAllResourceQueries,
        optimisticallyUpdateFilterAndSearchResourceQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticUpdateAllQueries.resetQueriesData();
          optimisticUpdateDirectQueries.resetQueriesData();
          optimisticUpdateAllResourceQueries.resetQueriesData();
          optimisticallyUpdateFilterAndSearchResourceQueries.resetQueriesData();
        },
        invalidateQueries: () => {
          optimisticUpdateAllQueries.invalidateQueries();
          optimisticUpdateDirectQueries.invalidateQueries();
          optimisticUpdateAllResourceQueries.invalidateQueries();
          optimisticallyUpdateFilterAndSearchResourceQueries.invalidateQueries();
        }
      };
    },
    addNewTagToCache: (tag) => {
      queryClient.cancelQueries({
        predicate: (q) => tagsQueryPredicates.all(q) || tagsQueryPredicates.tag(q, tag.id),
        type: "active"
      });
      const optimisticUpdateAllQueries = optimisticallyUpdateQueries(tagsQueryPredicates.all, (data) => upsertItemInArrayById(data ?? [], tag, {
        createIfNotFound: true
      }));
      const optimisticUpdateDirectQueries = optimisticallyUpdateQueries((q) => tagsQueryPredicates.tag(q, tag.id), (data) => ({
        ...data,
        ...tag
      }));
      return {
        optimisticUpdateDirectQueries,
        optimisticUpdateAllQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticUpdateAllQueries.resetQueriesData();
          optimisticUpdateDirectQueries.resetQueriesData();
        },
        invalidateQueries: () => {
          optimisticUpdateAllQueries.invalidateQueries();
          optimisticUpdateDirectQueries.invalidateQueries();
        }
      };
    },
    replaceTagInCache: (oldTag, newTag) => {
      queryClient.cancelQueries({
        predicate: (q) => tagsQueryPredicates.all(q) || tagsQueryPredicates.tag(q, oldTag.id),
        type: "active"
      });
      const optimisticUpdateAllQueries = optimisticallyUpdateQueries(tagsQueryPredicates.all, (data) => data && data.map((t) => t.id === oldTag.id ? newTag : t));
      const optimisticUpdateDirectQueries = optimisticallyUpdateQueries((q) => tagsQueryPredicates.tag(q, oldTag.id), () => newTag);
      return {
        optimisticUpdateDirectQueries,
        optimisticUpdateAllQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticUpdateAllQueries.resetQueriesData();
          optimisticUpdateDirectQueries.resetQueriesData();
        },
        invalidateQueries: () => {
          optimisticUpdateAllQueries.invalidateQueries();
          optimisticUpdateDirectQueries.invalidateQueries();
        }
      };
    },
    assignTagFromResources: (tag, resourceIds, operation) => {
      queryClient.cancelQueries({
        predicate: (q) => tagsQueryPredicates.resources(q, resourceIds),
        type: "active"
      });
      const optimisticUpdateAllQueries = optimisticallyUpdateQueries((q) => tagsQueryPredicates.resources(q, resourceIds), (data) => {
        if (operation === "assign") {
          const nextData = (data || []).map((resource) => ({
            ...resource,
            tags: [
              ...resource.tags,
              tag
            ]
          }));
          resourceIds.forEach((resourceId) => {
            if (!nextData.find((item) => item.id === resourceId)) {
              nextData.push({
                id: resourceId,
                tags: [
                  tag
                ]
              });
            }
          });
          return nextData;
        } else {
          return (data || []).map((resource) => {
            return {
              ...resource,
              tags: resource.tags.filter((t) => t.id !== tag.id)
            };
          });
        }
      });
      const resourceListQueries = optimisticallyUpdateQueries((q) => resourceQueryPredicates.resourceListAny(q), (data) => {
        return data ? {
          ...data,
          pages: data.pages.map((page) => {
            return {
              ...page,
              resources: page.resources.map((resource) => {
                if (resourceIds.includes(resource.id)) {
                  return mutateResourceTag(resource, tag, operation);
                }
                return resource;
              })
            };
          })
        } : data;
      });
      const resourceDetailQueries = optimisticallyUpdateQueries((q) => resourceQueryPredicates.resourceMultiple(q, resourceIds), (data) => {
        return data ? mutateResourceTag(data, tag, operation) : data;
      });
      return {
        optimisticUpdateAllQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticUpdateAllQueries.resetQueriesData();
          resourceListQueries.resetQueriesData();
          resourceDetailQueries.resetQueriesData();
        },
        invalidateQueries: () => {
          optimisticUpdateAllQueries.invalidateQueries();
          resourceListQueries.invalidateQueries();
          resourceDetailQueries.invalidateQueries();
        }
      };
    }
  };
};

export { useQueryCacheTagHelpers };
//# sourceMappingURL=useQueryCacheTagHelpers.js.map
